import { lazy } from "react";
import { authRoles } from "./auth/authRoles";
import Loadable from "./components/Loadable";
import MatxLayout from "./components/MatxLayout/MatxLayout";
import JobListings from "./views/jobs/JobListings";
import ProtectedRoute from "./components/ProtectedRoute";
import JwtRegisterCandidate from "./views/sessions/JwtRegisterCandidate";
import JwtRegisterCompany from "./views/sessions/JwtRegisterCompany";
import JwtLoginCandidate from "./views/sessions/JwtLoginCandidate";
import JwtLoginCompany from "./views/sessions/JwtLoginCompany";
import ErrorContextProvider from "./contexts/ErrorContext";
import RedirectOnRole from "./components/RedirectOnRole";
import JobDetail from "./views/jobs/JobDetail";
import Jobs from "./views/jobs/Jobs";
import CompanyDetail from "./views/company/CompanyDetail";

// SESSION PAGES
const NotFound = Loadable(lazy(() => import("app/views/sessions/NotFound")));
const JwtLogin = Loadable(lazy(() => import("app/views/sessions/JwtLogin")));
const ForgotPassword = Loadable(lazy(() => import("app/views/sessions/ForgotPassword")));
// DASHBOARD PAGE
const Analytics = Loadable(lazy(() => import("app/views/dashboard/Analytics")));

// PRIVACY POLICY
const PrivacyPolicy = Loadable(lazy(() => import("app/utils/PrivacyPolicy")));

// INTERVIEW LIST
const InterviewList = Loadable(lazy(() => import("app/views/applied_candidate/InterviewList")));

// BILLING
const Checkout = Loadable(lazy(() => import("app/views/billing/Checkout")));

// Invoice
const InvoiceList = Loadable(lazy(() => import("app/views/invoice/InvoiceList")));
const CreateInvoice = Loadable(lazy(() => import("app/views/invoice/CreateInvoice")));
const InvoiceDetail = Loadable(lazy(() => import("app/views/invoice/InvoiceDetail")));

// PROFILE
const Profile = Loadable(lazy(() => import("app/views/profile/Profile")));

// TRASH
const TrashList = Loadable(lazy(() => import("app/views/trash/TrashList")));
const DeletedCompanyDetail = Loadable(lazy(() => import("app/views/trash/DeletedCompanyDetail")));

// REPORT
const ReportDetail = Loadable(lazy(() => import("app/views/report/ReportDetail")));
const ReportList = Loadable(lazy(() => import("app/views/report/ReportList")));

// CANDIDATE
const AddCandidate = Loadable(lazy(() => import("app/views/candidate/AddCandidate")));
const CandidateDetailPage = Loadable(lazy(() => import("app/views/candidate/CandidateDetailPage")));
const CandidateList = Loadable(lazy(() => import("app/views/candidate/CandidateList")));

// COMPANY
const AddCompany = Loadable(lazy(() => import("app/views/company/AddCompany")));
const CompanyList = Loadable(lazy(() => import("app/views/company/CompanyList")));
const CompanyJobListings = Loadable(lazy(() => import("app/views/jobs/CompanyJobListings")));
const RecruitementMetricDetail = Loadable(
  lazy(() => import("app/views/recruitement_metric_detail/RecruitemenMetricDetail"))
);

// APPLICATION
const AppliedCandidate = Loadable(
  lazy(() => import("app/views/applied_candidate/AppliedCandidate"))
);
const ApplicationDetail = Loadable(
  lazy(() => import("app/views/applied_candidate/ApplicationDetail"))
);
const YourApplications = Loadable(
  lazy(() => import("app/views/your_application/YourApplications"))
);

// JOB
const ApplyJob = Loadable(lazy(() => import("app/views/jobs/ApplyJob")));
const PostJob = Loadable(lazy(() => import("app/views/jobs/PostJob")));
const JobDetailQuickApply = Loadable(lazy(() => import("app/views/jobs/JobDetailQuickApply")));
const JobDetailMultiApply = Loadable(lazy(() => import("app/views/jobs/JobDetailMultiApply")));
const SavedJobs = Loadable(lazy(() => import("app/views/jobs/SavedJobs")));

// AI Screening
const AiScreening = Loadable(lazy(() => import("app/views/ai_screening/AiScreening")));

// Management Pages
const SkillManagement = Loadable(lazy(() => import("app/views/management/SkillManagement")));
const BenefitsManagement = Loadable(lazy(() => import("app/views/management/BenefitsManagement")));

const routes = [
  { path: "/", element: <RedirectOnRole /> },
  {
    element: <MatxLayout />,
    children: [
      {
        path: "/jobs",
        element: <Jobs />
      },
      {
        path: "/jobs/:search?",
        element: <Jobs />
      },
      {
        path: "/privacyPolicy",
        element: <PrivacyPolicy />
      },
      {
        path: "/company/:id",
        element: <CompanyDetail />
      },
      {
        path: "/dashboard/default",
        element: (
          <ProtectedRoute authRoles={authRoles.company.concat(authRoles.candidate)}>
            <Analytics />
          </ProtectedRoute>
        ),
        auth: authRoles.admin
      },
      {
        path: "/applications/current/:role",
        element: (
          <ProtectedRoute authRoles={authRoles.company.concat(authRoles.candidate)}>
            <AppliedCandidate />
          </ProtectedRoute>
        )
      },
      {
        path: "/applications/detail/:id",
        element: (
          <ProtectedRoute authRoles={authRoles.company.concat(authRoles.candidate)}>
            <ApplicationDetail />
          </ProtectedRoute>
        )
      },
      {
        path: "/jobs/apply",
        element: (
          <ProtectedRoute authRoles={authRoles.candidate}>
            <ApplyJob />
          </ProtectedRoute>
        )
      },
      {
        path: "/interviews/list",
        element: (
          <ProtectedRoute authRoles={authRoles.company.concat(authRoles.candidate)}>
            <InterviewList />
          </ProtectedRoute>
        )
      },
      // jobs route
      {
        path: "/jobs/listings/:role",
        element: (
          <ProtectedRoute authRoles={authRoles.candidate}>
            <JobListings />
          </ProtectedRoute>
        )
      },
      {
        path: "/jobs/saved",
        element: (
          <ProtectedRoute authRoles={authRoles.candidate}>
            <SavedJobs />
          </ProtectedRoute>
        )
      },
      {
        path: "/jobs/job-detail/:job/:id",
        element: <JobDetail />
      },
      {
        path: "/jobs/job-detail-qa/:job/:id",
        element: <JobDetailQuickApply />
      },
      {
        path: "/jobs/job-apply",
        element: <JobDetailMultiApply />
      },
      {
        path: "/jobs/listings/company",
        element: (
          <ProtectedRoute authRoles={authRoles.company.concat(authRoles.candidate)}>
            <CompanyJobListings />
          </ProtectedRoute>
        )
      },
      {
        path: "/jobs/candidate/applied",
        element: (
          <ProtectedRoute authRoles={authRoles.candidate}>
            <YourApplications />
          </ProtectedRoute>
        )
      },
      {
        path: "/jobs/company/applied",
        element: (
          <ProtectedRoute authRoles={authRoles.company}>
            <AppliedCandidate />
          </ProtectedRoute>
        )
      },
      {
        path: "/jobs/postjob",
        element: (
          <ProtectedRoute authRoles={authRoles.company}>
            <PostJob />
          </ProtectedRoute>
        )
      },
      // ai screening routes
      {
        path: "/jobs/:jobId/ai-screening",
        element: (
          <ProtectedRoute authRoles={authRoles.company}>
            <AiScreening />
          </ProtectedRoute>
        )
      },
      // candidate route
      {
        path: "/candidate/listings/:role",
        element: (
          <ProtectedRoute authRoles={authRoles.admin}>
            <CandidateList />
          </ProtectedRoute>
        )
      },
      {
        path: "/candidate/detail/:id",
        element: <CandidateDetailPage />
      },
      {
        path: "/candidate/addCandidate",
        element: (
          <ProtectedRoute authRoles={authRoles.admin}>
            <AddCandidate />
          </ProtectedRoute>
        ),
        auth: authRoles.admin
      },
      // company route
      {
        path: "/company/listings/:role",
        element: (
          <ProtectedRoute authRoles={authRoles.admin}>
            <CompanyList />
          </ProtectedRoute>
        )
      },
      {
        path: "/company/addCompany",
        element: (
          <ProtectedRoute authRoles={authRoles.admin}>
            <AddCompany />
          </ProtectedRoute>
        )
      },
      {
        path: "/recruitement-metric-detail",
        element: (
          <ProtectedRoute authRoles={authRoles.company}>
            <RecruitementMetricDetail />
          </ProtectedRoute>
        )
      },
      {
        path: "/profile",
        element: (
          <ProtectedRoute authRoles={authRoles.company.concat(authRoles.candidate)}>
            <Profile />
          </ProtectedRoute>
        )
      },
      {
        path: "/profile/:tab",
        element: (
          <ProtectedRoute authRoles={authRoles.company.concat(authRoles.candidate)}>
            <Profile />
          </ProtectedRoute>
        )
      },
      // Invoice/Payment
      {
        path: "/invoice/create",
        element: (
          <ProtectedRoute authRoles={authRoles.admin}>
            <CreateInvoice />
          </ProtectedRoute>
        )
      },
      {
        path: "/invoice/:id/detail",
        element: (
          <ProtectedRoute authRoles={authRoles.admin.concat(authRoles.company)}>
            <InvoiceDetail />
          </ProtectedRoute>
        )
      },
      {
        path: "/invoice",
        element: (
          <ProtectedRoute authRoles={authRoles.admin.concat(authRoles.company)}>
            <InvoiceList />
          </ProtectedRoute>
        )
      },
      // Report
      {
        path: "/report",
        element: (
          <ProtectedRoute authRoles={authRoles.admin}>
            <ReportList />
          </ProtectedRoute>
        )
      },
      {
        path: "/report/:applicationId",
        element: (
          <ProtectedRoute authRoles={authRoles.admin}>
            <ReportDetail />
          </ProtectedRoute>
        )
      },
      // Management Routes
      {
        path: "/skill-management",
        element: (
          <ProtectedRoute authRoles={authRoles.admin}>
            <SkillManagement />
          </ProtectedRoute>
        )
      },
      {
        path: "/benefit-management",
        element: (
          <ProtectedRoute authRoles={authRoles.admin}>
            <BenefitsManagement />
          </ProtectedRoute>
        )
      },
      // Billing Routes

      {
        path: "/checkout/payment",
        element: (
          <ProtectedRoute authRoles={authRoles.company.concat(authRoles.candidate)}>
            <Checkout />
          </ProtectedRoute>
        )
      },
      // Trash routes
      {
        path: "/trash",
        element: (
          <ProtectedRoute authRoles={authRoles.admin.concat(authRoles.company)}>
            <TrashList />
          </ProtectedRoute>
        )
      },
      {
        path: "/company/deleted/:id",
        element: (
          <ProtectedRoute authRoles={authRoles.admin.concat(authRoles.company)}>
            <DeletedCompanyDetail />
          </ProtectedRoute>
        )
      }
    ]
  },

  // session pages route
  { path: "/session/404", element: <NotFound /> },
  { path: "/session/login/admin", element: <JwtLogin /> },
  { path: "/session/login/candidate", element: <JwtLoginCandidate /> },
  { path: "/session/login/company", element: <JwtLoginCompany /> },
  {
    path: "/session/signup/candidate",
    element: (
      <ErrorContextProvider>
        <JwtRegisterCandidate />
      </ErrorContextProvider>
    )
  },
  {
    path: "/session/signup/company",
    element: (
      <ErrorContextProvider>
        <JwtRegisterCompany />
      </ErrorContextProvider>
    )
  },
  { path: "/session/forgot-password", element: <ForgotPassword /> },
  { path: "*", element: <NotFound /> }
];

export default routes;
